/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@vector-im/compound-web";
import { PopOutIcon } from "@vector-im/compound-design-tokens/assets/web/icons";
import { logger } from "matrix-js-sdk/lib/logger";

import { useClient } from "../ClientContext";
import { Modal } from "../Modal";
import { useRoomEncryptionSystem } from "../e2ee/sharedKeyManagement";
import { getAbsoluteRoomUrl } from "../utils/matrix";
import styles from "./AppSelectionModal.module.css";
import { editFragmentQuery } from "../UrlParams";
import { E2eeType } from "../e2ee/e2eeType";
import { getHomeserverAssociatedDomain } from "../knownHomeserversMap";
import { Config } from "../config/Config";

interface Props {
  roomId: string;
}

/**
 * Строит ссылку для открытия комнаты в приложении Klava.
 * Для известных homeserver'ов переводит ссылку на web-домен,
 * где уже настроены universal/app links мобильных клиентов.
 */
export function buildAppSelectionUrl(
  browserRoomUrl: string,
  homeserver?: string,
): string {
  const url = new URL(browserRoomUrl);

  if (homeserver) {
    const webDomain = getHomeserverAssociatedDomain(homeserver, "web");

    if (webDomain !== window.location.host) {
      url.protocol = "https:";
      url.host = webDomain;
      url.port = "";
    }
  }

  return url.toString();
}

export const AppSelectionModal: FC<Props> = ({ roomId }) => {
  const { t } = useTranslation();
  const { client } = useClient();

  const [open, setOpen] = useState(true);
  const onBrowserClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    },
    [setOpen],
  );
  const e2eeSystem = useRoomEncryptionSystem(roomId);
  const homeserver = client?.getDomain() ?? Config.defaultServerName();

  if (e2eeSystem.kind === E2eeType.NONE) {
    logger.error(
      "Generating app redirect URL for encrypted room but don't have key available!",
    );
  }

  const appUrl = useMemo(() => {
    const url = new URL(getAbsoluteRoomUrl(roomId, e2eeSystem));
    // Отключаем повторный prompt внутри приложения и фиксируем пользователя в комнате.
    url.hash = editFragmentQuery(url.hash, (params) => {
      params.set("appPrompt", "false");
      params.set("confineToRoom", "true");
      return params;
    });

    return buildAppSelectionUrl(url.toString(), homeserver);
  }, [e2eeSystem, homeserver, roomId]);

  return (
    <Modal
      className={styles.modal}
      title={t("app_selection_modal.title")}
      open={open}
    >
      <Text size="md" weight="semibold">
        {t("app_selection_modal.text")}
      </Text>
      <Button kind="secondary" onClick={onBrowserClick}>
        {t("app_selection_modal.continue_in_browser")}
      </Button>
      <Button as="a" href={appUrl} Icon={PopOutIcon}>
        {t("app_selection_modal.open_in_app")}
      </Button>
    </Modal>
  );
};
